import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Tooltip,
  FormControl,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSearch } from 'react-icons/fa';
import { Container, Header, HeaderLeft, BackButton, Title, Content, ControlsContainer } from '../../common/styles';
import { 
  MainContainer, 
  IssuersGrid, 
  IssuerCard, 
  IssuerCardContent, 
  IssuerTitle, 
  EmployeeBadge,
  ActionButtons,
  UsageBadgesContainer,
  UsageBadge,
  CreatedAtText
} from './styles';
import { COLORS } from '../../../constants/radar';
import { useAlert } from '../../../contexts/AlertContext';
import { cvManagementApi } from '../../../services/api';
import { getRelativeTime } from '../../../utils/dateUtils';
import LoadingSpinner from '../../common/LoadingSpinner';
import ConsultantAttachmentFilter from '../../common/filters/ConsultantAttachmentFilter';

const CertificationIssuerReconciliation = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [customIssuers, setCustomIssuers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentIssuer, setCurrentIssuer] = useState({ id: null, name: '' });
  const [selectedIssuer, setSelectedIssuer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('attached');
  const [showControls, setShowControls] = useState(true);
  const [standardIssuers, setStandardIssuers] = useState([]);
  const [loadingStandardIssuers, setLoadingStandardIssuers] = useState(false);

  // Load issuers on component mount
  useEffect(() => {
    fetchCustomIssuers();
  }, []);

  const fetchCustomIssuers = async () => {
    try {
      setLoading(true);
      // This API endpoint will need to be implemented
      const response = await cvManagementApi.getCustomCertificationIssuers();
      setCustomIssuers(response || []);
    } catch (error) {
      console.error('Error fetching custom certification issuers:', error);
      showAlert('Failed to load custom certification issuers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (issuer = null) => {
    if (issuer) {
      setCurrentIssuer({
        id: issuer.id,
        name: issuer.name
      });
      setSelectedIssuer(null);
    } else {
      setCurrentIssuer({ id: null, name: '' });
      setSelectedIssuer(null);
    }
    setOpenDialog(true);
    
    // Fetch standard certification issuers when dialog opens
    fetchStandardIssuers();
  };

  const fetchStandardIssuers = async () => {
    try {
      setLoadingStandardIssuers(true);
      const response = await cvManagementApi.getGeneralCertificationIssuers();
      setStandardIssuers(response || []);
    } catch (error) {
      console.error('Error fetching standard certification issuers:', error);
      showAlert('Failed to load standard certification issuers', 'error');
    } finally {
      setLoadingStandardIssuers(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedIssuer(null);
  };

  const handleIssuerChange = (event, newValue) => {
    setSelectedIssuer(newValue || null);
  };

  const handleReconcileIssuer = async () => {
    if (!selectedIssuer?.id) {
      showAlert('Please select an existing certification issuer to reconcile with', 'error');
      return;
    }

    try {
      // This API endpoint will need to be implemented
      await cvManagementApi.reconcileCertificationIssuer(
        currentIssuer.id, 
        selectedIssuer.id
      );
      
      showAlert('Certification issuer reconciled successfully', 'success');
      
      // Refresh issuers list
      await fetchCustomIssuers();
      handleCloseDialog();
    } catch (error) {
      console.error('Error reconciling certification issuer:', error);
      showAlert(`Failed to reconcile certification issuer: ${error.message}`, 'error');
    }
  };

  // Filter issuers based on search term and employee attachment
  const filteredIssuers = customIssuers.filter(issuer => {
    const isAttached = !!issuer.employee;

    if (employeeFilter === 'attached' && !isAttached) {
      return false;
    }
    if (employeeFilter === 'unattached' && isAttached) {
      return false;
    }

    const term = (searchTerm || '').toLowerCase();
    return issuer.name.toLowerCase().includes(term) ||
      (issuer.employee && issuer.employee.fullName.toLowerCase().includes(term)) ||
      (issuer.employeeCV && issuer.employeeCV.fullName.toLowerCase().includes(term));
  });

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/cv-configuration')}>
            <FaArrowLeft /> Back to CV Configuration
          </BackButton>
          <Title>Certification Issuer Reconciliation</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Certification Issuers"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 14px',
                  }
                }}
              />
              <ConsultantAttachmentFilter
                value={employeeFilter}
                onChange={setEmployeeFilter}
              />
            </Box>
          </Box>
        </ControlsContainer>

        <MainContainer>
          {!loading && (
            <div style={{
              color: '#64748B',
              fontSize: '14px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span style={{ fontWeight: 600 }}>
                {filteredIssuers.length}
              </span>
              {filteredIssuers.length === 1 ? 'issuer' : 'issuers'} found
            </div>
          )}

          {loading ? (
            <LoadingSpinner message="Loading certification issuers..." />
          ) : filteredIssuers.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px dashed #dee2e6'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#6c757d',
                  marginBottom: '8px',
                  fontWeight: 500
                }}
              >
                No certification issuers found
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#adb5bd',
                  maxWidth: '400px'
                }}
              >
                {searchTerm || employeeFilter !== 'all'
                  ? "No certification issuers match your current filters. Try adjusting your search or filter."
                  : "No custom certification issuers have been added yet."}
              </Typography>
            </Box>
          ) : (
            <IssuersGrid>
              {filteredIssuers.map((issuer) => (
                <IssuerCard key={issuer.id}>
                  <IssuerCardContent>
                    <Typography variant="caption" sx={{
                      color: '#94a3b8',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Issuer
                    </Typography>
                    <IssuerTitle>
                      {issuer.name}
                    </IssuerTitle>

                    {(issuer.employee || issuer.employeeCV) && (
                      <EmployeeBadge>
                        <PersonIcon fontSize="small" /> {issuer.employee?.fullName || issuer.employeeCV?.fullName}
                      </EmployeeBadge>
                    )}

                    {issuer.usageDetails?.certifications?.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{
                          color: '#94a3b8',
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          mb: 0.5,
                          display: 'block'
                        }}>
                          {issuer.usageDetails.certifications.length === 1 ? 'Certification' : 'Certifications'}
                        </Typography>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: '#f8fafc',
                          borderRadius: '4px',
                          border: '1px solid #e2e8f0',
                          transition: 'all 0.2s'
                        }} className="certification-box">
                        {issuer.usageDetails.certifications.map((cert) => (
                          <Box key={cert.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="subtitle2" sx={{
                              color: '#334155',
                              fontSize: '13px',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <SchoolIcon sx={{ fontSize: 16 }} />
                              {cert.name}
                            </Typography>
                            {cert.website && (
                              <Typography 
                                component="a" 
                                sx={{ 
                                  color: '#3b82f6',
                                  fontSize: '12px',
                                  textDecoration: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                }}
                              >
                                <LinkIcon sx={{ fontSize: 14 }} />
                                {cert.website}
                              </Typography>
                            )}
                          </Box>
                        ))}
                        </Box>
                      </Box>
                    )}

                    {issuer.createdAt && (
                      <CreatedAtText>
                        <AccessTimeIcon /> Added {getRelativeTime(issuer.createdAt)}
                      </CreatedAtText>
                    )}

                    <ActionButtons>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDialog(issuer)}
                        title="Reconcile with existing certification issuers"
                      >
                        <LinkIcon />
                      </IconButton>
                    </ActionButtons>
                  </IssuerCardContent>
                </IssuerCard>
              ))}
            </IssuersGrid>
          )}
        </MainContainer>
      </Content>

      {/* Reconcile Certification Issuer Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: { xs: '95%', sm: '80%', md: '800px' },
            m: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle>Reconcile Custom Certification Issuer</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Reconcile the custom certification issuer <strong>"{currentIssuer.name}"</strong> with existing certification issuers.
          </DialogContentText>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 500 }}>
              Select a Certification Issuer to Reconcile With
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              {loadingStandardIssuers ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Loading certification issuers...
                  </Typography>
                </Box>
              ) : (
                <Autocomplete
                  options={standardIssuers}
                  getOptionLabel={(option) => option.name || ''}
                  value={selectedIssuer}
                  onChange={handleIssuerChange}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Standard Certification Issuer"
                      placeholder="Search for an issuer..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.name}
                    </li>
                  )}
                  noOptionsText="No certification issuers found"
                  fullWidth
                />
              )}
            </FormControl>
            
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleReconcileIssuer} 
            variant="contained"
            startIcon={<LinkIcon />}
            disabled={!selectedIssuer?.id}
            sx={{ 
              bgcolor: COLORS.PRIMARY,
              '&:hover': {
                bgcolor: COLORS.PRIMARY_DARK,
              }
            }}
          >
            Reconcile
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CertificationIssuerReconciliation; 